import { useEffect, useMemo, useState } from 'react';
import { RealtimeRole } from '@doxyme/realtime';
import {
  MultiplayerCallCapability,
  MultiplayerRoomCapability,
  Specs,
} from '@doxyme/extensions-bridge';
import { useAppDispatch, useAppSelector } from '../../useReduxToolkit';
import { sendMessageToAllIdentities } from '../../../features/realtime/realtime.slice';
import { subscribeToMessage } from '../../../features/realtime/realtime.subscribe';
import {
  getRole,
  getIdentity,
} from '../../../features/realtime/realtime.selectors';
import {
  RealtimeMessageType,
  RealtimeMember,
} from '../../../features/realtime/realtime.types';
import { getChatUserIdFromIdentity } from '../../../features/realtime/realtime.helpers';
import { bridge } from '../../../features/extensions/instances';
import { session } from '../../../webrtc/session';

type Command = Specs.Multiplayer.Command;

const getMultiplayerRole = (
  rr: RealtimeRole | undefined,
): Specs.ReceiveContext.Role =>
  rr === RealtimeRole.Provider
    ? Specs.ReceiveContext.Role.Provider
    : Specs.ReceiveContext.Role.Patient;

const getMessageType = (id: string): RealtimeMessageType =>
  id === MultiplayerRoomCapability.id
    ? RealtimeMessageType.ExtensionRoomCommand
    : RealtimeMessageType.ExtensionCallCommand;

// There is a decent bit of Apollo-specific code necessary to wire the MP Capabilities access to the data they need.
// This type of code should remain inside the Apollo frontend codebase.
export function useExtensionsMultiplayer(
  Capability:
    | typeof MultiplayerRoomCapability
    | typeof MultiplayerCallCapability,
  members: RealtimeMember[],
): void {
  const dispatch = useAppDispatch();
  const role = useAppSelector(getRole);
  const identity = useAppSelector(getIdentity);
  const realtimeId = getChatUserIdFromIdentity(identity);
  const { sessionId } = session;

  const [capability, setCapability] = useState<
    MultiplayerCallCapability | MultiplayerRoomCapability | undefined
  >();

  const participants = useMemo(
    () =>
      members.map((m) => ({
        id: getChatUserIdFromIdentity(m.identity),
        username: m.state.username,
        avatar: m.picture,
        inviteId: m.state.inviteId,
        ip: m.state.privateData?.ip,
        ...(capability?.id === MultiplayerCallCapability.id && { sessionId }),
      })),
    [members, sessionId],
  );

  // If our role changes we need to reinitialize the capability appropriately.
  // This most commonly happens during initialization as our role starts undefined and later gets
  // set appropriately.
  useEffect(() => {
    if (role && realtimeId) {
      setCapability(new Capability(getMultiplayerRole(role), realtimeId));
    } else {
      setCapability(undefined);
    }
  }, [role, realtimeId, Capability]);

  // 1. When people come and go, inform the capability of participants
  useEffect(() => {
    capability?.updateParticipants(participants);
  }, [participants]);

  // When our capability changes we should plug it in/out of the bridge.
  // Note: This functionality could be brought into an improved version of the useCapability hook
  useEffect(() => {
    if (!capability) {
      return () => void 0;
    }

    bridge.addCapability(capability);
    capability.updateParticipants(participants);

    // 2. When we see one of our messages, relay it to the capability
    const unsubscribeFromMessage = subscribeToMessage({
      messageType: getMessageType(capability.id),
      callback: ({ payload }, event) => {
        capability.handleCommand(
          getChatUserIdFromIdentity(event.from),
          payload as Command,
        );
      },
    });

    // 3. When the capability produces a command, relay it via realtime
    const unsubscribeFromCommand = capability.onCommand(
      async (participantId: string, command: Command): Promise<void> => {
        await dispatch(
          sendMessageToAllIdentities({
            chatUserId: participantId,
            message: {
              messageType: getMessageType(capability.id),
              payload: command,
            },
          }),
        );
      },
    );

    return () => {
      bridge.removeCapability(capability.id);
      unsubscribeFromMessage();
      unsubscribeFromCommand();
    };
  }, [capability]);
}
